// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/settings/Setup2FAPage.tsx
//
// Phase 1 Task 49 — 2FA enrollment page.
// Method picker (TOTP / Email OTP) → enroll → verify code → recovery codes.
//
// Endpoints (API-CONTRACTS L974+ / L1087+):
//   POST /auth/2fa/setup-totp     → { enrollmentToken, secret, otpAuthUri, qrCodeDataUrl }
//   POST /auth/2fa/verify-totp    → { recoveryCodes[8] }
//   POST /auth/2fa/setup-email    → { enrollmentToken, email, otpExpiresAt }
//   POST /auth/2fa/verify-email   → { recoveryCodes[8] }
//
// All four are Tier A sensitive — `requireFreshPerms({ window: '5m' })`.
// Disable button (also sensitive — handle perm-stale + impersonate-denylist
// 403 per CONTEXT.md / D-R7-08): POST /auth/2fa/disable.
//
// Goal coverage:.

import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  useAuth,
  SettingsLayout,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';

type Method = 'TOTP' | 'EMAIL';
type Stage = 'pick' | 'enroll' | 'verify' | 'recovery' | 'disabled';

interface EnrollData {
  method: Method;
  enrollmentToken: string;
  // TOTP only
  qrCodeDataUrl?: string;
  otpAuthUri?: string;
  secret?: string;
  // EMAIL only
  email?: string;
}

type LooseClient = {
  POST: (p: string, init?: { body?: unknown }) => Promise<{
    data?: unknown;
    error?: unknown;
    response: Response;
  }>;
  GET: (p: string) => Promise<{ data?: unknown; response: Response }>;
  DELETE: (p: string) => Promise<{ data?: unknown; response: Response }>;
};

interface TrustedDeviceRow {
  id: string;
  userAgent: string;
  ip: string;
  createdAt: number;
  expiresAt: number;
  current: boolean;
}

const FRESH_ERROR_CODES = new Set([
  'AUTH_FRESH_PERMS_REQUIRED',
  'AUTH_PERM_VERSION_STALE',
]);

async function readErrorCode(response: Response): Promise<string> {
  try {
    const env = (await response.clone().json()) as
      | { error?: { code?: string } }
      | undefined;
    if (env?.error?.code) return env.error.code;
  } catch {
    // ignore
  }
  return `HTTP_${response.status}`;
}

export function Setup2FAPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const refresh = useAuth((s) => s.refresh);
  const fetchMe = useAuth((s) => s.fetchMe);
  // P8.D-508 — when the user landed here via the mandatory first-login
  // enrollment gate, finishing enrollment must mint an unrestricted token
  // (refresh) + re-hydrate /me so AuthGuard stops redirecting back here.
  const authUser = useAuth((s) => s.user) as
    | { mustSetup2FA?: boolean; twoFactorEnabled?: boolean }
    | null;
  // Whether 2FA is currently enabled on this account — drives the pick view:
  // enabled → show status + Disable only; not enabled → show method picker +
  // Start setup only (never surface Disable before enrollment).
  const alreadyEnabled = authUser?.twoFactorEnabled === true;

  const [method, setMethod] = useState<Method>('TOTP');
  const [stage, setStage] = useState<Stage>('pick');
  const [enroll, setEnroll] = useState<EnrollData | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Disable 2FA requires re-auth (password or current code) — BE schema
  // rejects an empty body. We collect the account password inline.
  const [disablePassword, setDisablePassword] = useState('');
  // #1137 — trusted-device list + per-device revoke.
  const [devices, setDevices] = useState<TrustedDeviceRow[] | null>(null);
  const [deviceBusyId, setDeviceBusyId] = useState<string | null>(null);

  const fetchTrustedDevices = async (): Promise<void> => {
    try {
      const client = apiClient() as unknown as LooseClient;
      const { data } = await client.GET('/api/v1/auth/2fa/trusted-devices');
      const env = data as { data?: { devices?: TrustedDeviceRow[] } } | undefined;
      setDevices(env?.data?.devices ?? []);
    } catch {
      setDevices([]);
    }
  };

  const revokeDevice = async (id: string): Promise<void> => {
    setDeviceBusyId(id);
    try {
      const client = apiClient() as unknown as LooseClient;
      await client.DELETE(`/api/v1/auth/2fa/trusted-devices/${id}`);
      await fetchTrustedDevices();
    } catch {
      // Non-fatal — list refetch on next mount will reconcile.
    } finally {
      setDeviceBusyId(null);
    }
  };

  useEffect(() => {
    if (alreadyEnabled) void fetchTrustedDevices();
    else setDevices(null);
    // fetchTrustedDevices is stable enough for this one-shot load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alreadyEnabled]);

  // Recovery codes — download as a .txt / copy to clipboard (shown once).
  const handleDownloadCodes = (): void => {
    const blob = new Blob([`${recoveryCodes.join('\n')}\n`], {
      type: 'text/plain;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'printway-2fa-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleCopyCodes = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — user can still download / copy manually.
    }
  };

  // -------------------------------------------------------------------------
  // POST helpers — wrapped to retry once on AUTH_FRESH_PERMS_REQUIRED.
  // -------------------------------------------------------------------------
  const postWithFreshRetry = async (
    path: string,
    body: Record<string, unknown>,
  ): Promise<{ ok: boolean; data: unknown; status: number; code?: string }> => {
    const client = apiClient() as unknown as LooseClient;
    let { data, response } = await client.POST(path, { body });
    if (!response.ok && (response.status === 401 || response.status === 428)) {
      const code = await readErrorCode(response);
      if (FRESH_ERROR_CODES.has(code) || response.status === 428) {
        await refresh();
        ({ data, response } = await client.POST(path, { body }));
      }
    }
    if (!response.ok) {
      const code = await readErrorCode(response);
      return { ok: false, data, status: response.status, code };
    }
    return { ok: true, data, status: response.status };
  };

  // -------------------------------------------------------------------------
  // Stage 1 — start enrollment
  // -------------------------------------------------------------------------
  const handleStartEnroll = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const path =
        method === 'TOTP'
          ? '/api/v1/auth/2fa/setup-totp'
          : '/api/v1/auth/2fa/setup-email';
      const body =
        method === 'TOTP' ? { label: 'PrintwayV3 (uadmin)' } : {};
      const res = await postWithFreshRetry(path, body);
      if (!res.ok) {
        // Impersonate-denylist 403 per D-R7-08: 2fa setup is on the denylist —
        // surface explicit message instead of generic.
        if (res.status === 403 && res.code === 'AUTH_IMPERSONATE_DENIED') {
          setError(t('setup2fa.errors.impersonateSetup'));
        } else {
          setError(res.code ?? `HTTP_${res.status}`);
        }
        return;
      }
      const env = res.data as { data?: Record<string, unknown> } | undefined;
      const payload = env?.data ?? {};
      const enrollmentToken = payload['enrollmentToken'];
      if (typeof enrollmentToken !== 'string') {
        setError('MALFORMED_RESPONSE');
        return;
      }
      const next: EnrollData = { method, enrollmentToken };
      if (method === 'TOTP') {
        next.qrCodeDataUrl = payload['qrCodeDataUrl'] as string | undefined;
        next.otpAuthUri = payload['otpAuthUri'] as string | undefined;
        next.secret = payload['secret'] as string | undefined;
      } else {
        next.email = payload['email'] as string | undefined;
      }
      setEnroll(next);
      setStage('enroll');
    } catch (err) {
      setError(err instanceof Error ? err.message : '2FA_SETUP_FAILED');
    } finally {
      setBusy(false);
    }
  };

  // -------------------------------------------------------------------------
  // Stage 2 — verify code → receive recovery codes
  // -------------------------------------------------------------------------
  const handleVerify = async (
    ev: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    ev.preventDefault();
    if (!enroll) return;
    setBusy(true);
    setError(null);
    try {
      const path =
        enroll.method === 'TOTP'
          ? '/api/v1/auth/2fa/verify-totp'
          : '/api/v1/auth/2fa/verify-email';
      const res = await postWithFreshRetry(path, {
        enrollmentToken: enroll.enrollmentToken,
        code,
      });
      if (!res.ok) {
        setError(res.code ?? `HTTP_${res.status}`);
        return;
      }
      const env = res.data as { data?: { recoveryCodes?: string[] } } | undefined;
      const codes = env?.data?.recoveryCodes;
      if (Array.isArray(codes)) setRecoveryCodes(codes);
      setStage('recovery');
      // P8.D-508 — enrollment cleared User.mustSetup2FA server-side.
      // Refresh mints an unrestricted token; /me re-hydrate drops the
      // flag from the store so AuthGuard unlocks the rest of the app.
      // Always re-hydrate so `twoFactorEnabled` in the store flips to true
      // → returning to the pick view now shows the Disable action, not setup.
      try {
        await refresh();
        await fetchMe();
      } catch {
        // Non-fatal — next navigation will trigger a refresh anyway.
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '2FA_VERIFY_FAILED');
    } finally {
      setBusy(false);
    }
  };

  // -------------------------------------------------------------------------
  // Disable 2FA — sensitive, perm-stale + denylist handling.
  // -------------------------------------------------------------------------
  const handleDisable = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const res = await postWithFreshRetry(
        '/api/v1/auth/2fa/disable',
        { password: disablePassword },
      );
      if (!res.ok) {
        if (res.status === 403 && res.code === 'AUTH_IMPERSONATE_DENIED') {
          setError(t('setup2fa.errors.impersonateDisable'));
        } else if (res.code === 'AUTH_INVALID_CREDENTIALS') {
          setError(t('setup2fa.errors.wrongPassword'));
        } else {
          setError(res.code ?? `HTTP_${res.status}`);
        }
        return;
      }
      setDisablePassword('');
      setStage('disabled');
      // Re-hydrate so `twoFactorEnabled` flips to false in the store.
      try {
        await fetchMe();
      } catch {
        // Non-fatal.
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '2FA_DISABLE_FAILED');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SettingsLayout portal="admin" activeTab="security">
      <Card>
        <CardHeader>
          <CardTitle>{t('setup2fa.card.title')}</CardTitle>
          <CardDescription>{t('setup2fa.card.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          {stage === 'pick' && alreadyEnabled ? (
            <div className="space-y-4" data-testid="2fa-enabled-status">
              <p className="text-sm font-medium text-pw-text">
                {t('setup2fa.status.enabledTitle')}
              </p>
              <p className="text-sm text-pw-text-muted">
                {t('setup2fa.status.enabledDesc')}
              </p>
              <FormField>
                <FormLabel htmlFor="disable-password">
                  {t('setup2fa.disableConfirm.passwordLabel')}
                </FormLabel>
                <FormControl>
                  <Input
                    id="disable-password"
                    data-testid="2fa-disable-password"
                    type="password"
                    autoComplete="current-password"
                    placeholder={t('setup2fa.disableConfirm.passwordPlaceholder')}
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                  />
                </FormControl>
              </FormField>
              {error ? (
                <FormMessage data-testid="2fa-error">{error}</FormMessage>
              ) : null}
              <div className="flex justify-end">
                <Button
                  variant="destructive"
                  onClick={() => void handleDisable()}
                  disabled={busy || disablePassword.length < 8}
                  data-testid="2fa-disable"
                >
                  {busy ? t('setup2fa.buttons.processing') : t('setup2fa.buttons.disable')}
                </Button>
              </div>
            </div>
          ) : null}

          {stage === 'pick' && !alreadyEnabled ? (
            <div className="space-y-4">
              <FormField>
                <FormLabel htmlFor="method">{t('setup2fa.fields.method')}</FormLabel>
                <FormControl>
                  <Select
                    value={method}
                    onValueChange={(value) => setMethod(value as Method)}
                  >
                    <SelectTrigger id="method" data-testid="2fa-method-picker">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TOTP">{t('setup2fa.methodOptions.totp')}</SelectItem>
                      <SelectItem value="EMAIL">{t('setup2fa.methodOptions.email')}</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormField>
              {error ? (
                <FormMessage data-testid="2fa-error">{error}</FormMessage>
              ) : null}
              <div className="flex justify-end">
                <Button
                  onClick={() => void handleStartEnroll()}
                  disabled={busy}
                  data-testid="2fa-start-enroll"
                >
                  {busy ? t('setup2fa.buttons.processing') : t('setup2fa.buttons.startEnroll')}
                </Button>
              </div>
            </div>
          ) : null}

          {stage === 'enroll' && enroll ? (
            <Form onSubmit={handleVerify} data-testid="2fa-verify-form">
              {enroll.method === 'TOTP' ? (
                <>
                  <p className="text-sm text-pw-text">
                    {t('setup2fa.enroll.totpHint')}
                  </p>
                  {enroll.qrCodeDataUrl ? (
                    <img
                      src={enroll.qrCodeDataUrl}
                      alt={t('setup2fa.enroll.qrAlt')}
                      data-testid="2fa-qr-image"
                      className="mx-auto h-48 w-48 rounded-md border border-pw-border bg-white p-2" /* dark-ok: QR code image must render on white to remain scannable (P999.11.D-04) */
                    />
                  ) : null}
                  {enroll.secret ? (
                    <p className="text-center font-mono text-xs text-pw-text-muted">
                      {t('setup2fa.enroll.secretLabel', { secret: enroll.secret })}
                    </p>
                  ) : null}
                </>
              ) : (
                <p className="text-sm text-pw-text" data-testid="2fa-email-hint">
                  {t('setup2fa.enroll.emailHintBefore')}
                  <strong>{enroll.email ?? t('setup2fa.enroll.emailFallback')}</strong>
                  {t('setup2fa.enroll.emailHintAfter')}
                </p>
              )}

              <FormField>
                <FormLabel htmlFor="code">{t('setup2fa.fields.code')}</FormLabel>
                <FormControl>
                  <Input
                    id="code"
                    data-testid="2fa-code-input"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  />
                </FormControl>
              </FormField>

              {error ? (
                <FormMessage data-testid="2fa-error">{error}</FormMessage>
              ) : null}

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setStage('pick');
                    setEnroll(null);
                    setCode('');
                  }}
                  disabled={busy}
                >
                  {t('setup2fa.buttons.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={busy || code.length !== 6}
                  data-testid="2fa-verify-submit"
                >
                  {busy ? t('setup2fa.buttons.verifying') : t('setup2fa.buttons.verify')}
                </Button>
              </div>
            </Form>
          ) : null}

          {stage === 'recovery' ? (
            <div className="space-y-4" data-testid="2fa-recovery-codes">
              <p className="text-sm text-pw-text">
                <strong>{t('setup2fa.recovery.successBold')}</strong>
                {t('setup2fa.recovery.note')}
              </p>
              <ul className="grid grid-cols-2 gap-2 rounded-md border border-pw-border bg-pw-surface p-3 font-mono text-xs">
                {recoveryCodes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="secondary"
                  onClick={() => void handleCopyCodes()}
                  data-testid="2fa-recovery-copy"
                >
                  {copied ? t('setup2fa.buttons.copied') : t('setup2fa.buttons.copy')}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleDownloadCodes}
                  data-testid="2fa-recovery-download"
                >
                  {t('setup2fa.buttons.download')}
                </Button>
                <Button
                  onClick={() => {
                    setStage('pick');
                    setEnroll(null);
                    setCode('');
                  }}
                >
                  {t('setup2fa.buttons.done')}
                </Button>
              </div>
            </div>
          ) : null}

          {stage === 'disabled' ? (
            <p
              className="text-sm font-medium text-green-600 dark:text-green-300"
              data-testid="2fa-disabled-confirm"
            >
              {t('setup2fa.disabled.message')}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {alreadyEnabled && devices && devices.length > 0 ? (
        <Card className="mt-4" data-testid="2fa-trusted-devices">
          <CardHeader>
            <CardTitle>{t('setup2fa.trustedDevices.title')}</CardTitle>
            <CardDescription>
              {t('setup2fa.trustedDevices.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-pw-border">
              {devices.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between gap-3 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm text-pw-text">
                      {d.userAgent || t('setup2fa.trustedDevices.unknownDevice')}
                      {d.current ? (
                        <span className="ml-2 rounded bg-pw-surface px-1.5 py-0.5 text-xs text-pw-text-muted">
                          {t('setup2fa.trustedDevices.current')}
                        </span>
                      ) : null}
                    </p>
                    <p className="text-xs text-pw-text-muted">
                      {d.ip}
                      {d.createdAt > 0
                        ? ` · ${new Date(d.createdAt).toLocaleString(undefined, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })}`
                        : ''}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void revokeDevice(d.id)}
                    disabled={deviceBusyId === d.id}
                    data-testid={`2fa-trusted-revoke-${d.id}`}
                  >
                    {t('setup2fa.trustedDevices.revoke')}
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </SettingsLayout>
  );
}
